import View from '../Support/View/View';

export default class FilterBar extends View {
    #defaultValue = 'default';
    #inputs       = {};
    #selected     = new Set();
    #multiple     = true;

    static get viewName() {
        return 'filter-bar';
    }
    static get viewType() {
        return 'component';
    }

    start() {
        this.#multiple = (this.viewData.multiple ?? true);

        this.#setInputs();
        this.#setInitiallySelected();

        this.$el.addEventListener('change', this.#onChange.bind(this));
        this.addEventHandler('submit', this.#onSubmit.bind(this));

        window.addEventListener('popstate', this.#onPopState.bind(this));
    }

    #setInputs() {
        this.$el.querySelectorAll('[name="selector[]"]').forEach(input => {
            this.#inputs[input.value] = input;
        });
    }

    #setInitiallySelected() {
        Object.entries(this.#inputs)
            .filter(([value, input]) => (!!input.checked && (value !== this.#defaultValue)))
            .forEach(([value, input]) => this.#setSelected(value, true));
    }

    /**
     * @param {Event} e
     */
    #onChange({ target: { value, checked } }) {
        this.#setSelected(value, checked);
        this.#updateInputs(value, checked);

        this.#setHistoryState();
        this.#dispatchSelectedEvent();
    }

    /**
     * @param {SubmitEvent} e
     */
    #onSubmit(e) {
        e.preventDefault();
    }

    /**
     * @param {PopStateEvent} e
     */
    #onPopState({ state }) {
        this.#selected.clear();

        (state?.values || []).forEach(value => {
            this.#setSelected(value, true);
            this.#updateInputs(value, true);
        });

        this.#dispatchSelectedEvent();
    }

    /**
     * @param {string} value
     * @param {boolean} isSelected
     */
    #setSelected(value, isSelected) {
        if (value === this.#defaultValue) {
            return this.#selected.clear();
        }

        if (!isSelected) {
            return this.#selected.delete(value);
        }

        if (this.#multiple) {
            return this.#selected.add(value);
        }

        this.#selected.clear();
        this.#selected.add(value);
    }

    /**
     * @param {string} value
     * @param {boolean} isSelected
     */
    #updateInputs(value, isSelected) {
        Object.entries(this.#inputs).forEach(([value, input]) => input.checked = this.#selected.has(value));

        if (value === this.#defaultValue) {
            this.#inputs[value].checked = isSelected;
        }
    }

    #setHistoryState() {
        const values = Array.from(this.#selected.values());

        const params = new URLSearchParams(/* location.search */ '');

        params.set('selected', values.join(','));
        params.set('page', +this.dataset.currentPage || 1);

        history.pushState({ values: !values.length ? [this.#defaultValue] : values }, '', `${location.origin}${location.pathname}?${params}`);
    }

    #dispatchSelectedEvent() {
        const event = new CustomEvent('view:filter-bar.selected', {
            bubbles: true,
            delegateTarget: this.$el,
            detail: {
                values: Array.from(this.#selected.values()),
            },
        });

        this.dispatchEvent(event);
    }
}